import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import api3001 from "../../services/api3001";
import api3004 from "../../services/api3004";
import OrcamentoDetailsModal from "../OrcamentoDetailsModal";

/* -------------------- CORES -------------------- */

const COLORS = {
  reservado: "#3085d6",
  terceiro: "#7c3aed",
  prereservado: "#facc15",
  executado: "#16a34a",
  normal: "#ffffff"
};

const getBackgroundColor = (tipo) => {
  const color = COLORS[tipo];
  if (!color) return "#fff";
  return color + "33"; // opacidade ~20%
};

/* -------------------- ESTILOS -------------------- */

const Painel = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  ${({ $focus }) =>
    $focus &&
    `
    width: 90vw;
    height: 90vh;
    position: fixed;
    top: 53%;
    left: 52%;
    transform: translate(-50%, -50%);
  `}
`;

const Titulo = styled.h2`
  font-size: 22px;
  margin: 10px 0;
  color: ${({ $focus }) => ($focus ? "#ffffff" : "#333")};
`;

const Controls = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
`;

const Btn = styled.button`
  padding: 4px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #f9f9f9;
  cursor: pointer;

  &:hover {
    background: #e6e6e6;
  }
`;

const Strong = styled.strong`
  text-transform: capitalize;
`;

const TabelaWrapper = styled.div`
  width: 100%;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;

  max-width: 70vw;
  max-height: 60vh;

  ${({ $focus }) =>
    $focus &&
    `
    max-width: 90vw;
    max-height: 90vh;
  `}
`;

const Tabela = styled.div`
  display: table;
  width: 100%;
`;

const Linha = styled.div`
  display: table-row;
`;

const Celula = styled.div`
  display: table-cell;
  border: 1px solid #eee;
  text-align: center;
  min-width: 50px;
  height: 60px;
  padding: 4px 6px;
  background: ${({ tipo }) => getBackgroundColor(tipo)};
`;

const HeaderCell = styled(Celula)`
  font-weight: bold;
  background: #f8f8f8;
  position: sticky;
  top: 0;
  z-index: 3;
`;

const FixedColumn = styled.div`
  display: table-cell;
  position: sticky;
  left: 0;
  background: #ddd;
  font-weight: bold;
  min-width: 140px;
  z-index: 5;
  padding: 4px 6px;
`;

const ReservationBlock = styled.div`
  margin: 2px auto;
  border-radius: 4px;
  color: white;
  font-size: 9px;
  padding: 2px 3px;
  background: ${({ tipo }) => COLORS[tipo] || "#999"};
  cursor: pointer;
`;

const CornerCell = styled(Celula)`
  position: sticky;
  top: 0;
  left: 0;
  background: #ddd;
  min-width: 140px;
  z-index: 5;
`;

const Legend = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
`;

const LegendColor = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  background: ${({ color }) => color};
  border: 1px solid #ccc;
`;

const FullScreen = styled.div`
  position: absolute;
  top: 30px;
  right: 30px;
  width: 28px;
  height: 28px;
  background-image: url(${({ $focus }) =>
    $focus ? "/exit.svg" : "/fullscreen.svg"});
  background-size: cover;
  cursor: pointer;
  z-index: 20;

  &:hover {
    transform: scale(1.1);
  }
`;

/* -------------------- DATA -------------------- */

const normalizarData = (str) => {
  const d = new Date(str);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/* -------------------- COMPONENTE -------------------- */

const Agenda = ({ $setFocus, $focus }) => {
  const [orcamentos, setOrcamentos] = useState([]);
  const [onibusMap, setOnibusMap] = useState({});
  const [showOrcamentoDetails, setShowOrcamentoDetails] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [reloadKey, setReloadKey] = useState(false);

  const token = localStorage.getItem("token");

  /* -------------------- BUSCAR ONIBUS -------------------- */

  useEffect(() => {
    api3004.get("/api/onibus").then((res) => {
      const map = {};
      res.data.forEach(bus => {
        map[bus.id] = {
          prefixo: bus.prefixo,
          placa: bus.placa
        };
      });
      setOnibusMap(map);
    });
  }, []);

  /* -------------------- BUSCAR ORÇAMENTOS -------------------- */

  useEffect(() => {
    api3001
      .get("/api/orcamento", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const filtrados = res.data
          .filter((o) =>
            ["reservado", "terceiro", "prereservado", "executado"].includes(
              o.status_viagem
            )
          )
          .map((o) => ({
            id: o.id,
            prefixo: onibusMap[o.onibus_id]?.prefixo || null,
            placa: onibusMap[o.onibus_id]?.placa || null,
            nome_terceiro: o.nome_terceiro,
            status_viagem: o.status_viagem,
            data_inicial: normalizarData(o.data_inicio),
            data_final: normalizarData(o.data_fim),
            cliente_nome: o.cliente_nome,
          }));

        setOrcamentos(filtrados);
      });
  }, [token, reloadKey, onibusMap]);

  const reload = () => setReloadKey(!reloadKey);

  /* -------------------- AGRUPAMENTO -------------------- */

  const grouped = useMemo(() => {
    const executadosMap = {};
    const reservadosMap = {};
    const terceirosMap = {};
    const preReservados = [];

    orcamentos.forEach((o) => {
      if (o.status_viagem === "prereservado") preReservados.push(o);

      if (o.status_viagem === "reservado" && o.prefixo) {
        const key = `Reservado: ${o.prefixo} | ${o.placa}`;
        if (!reservadosMap[key]) reservadosMap[key] = [];
        reservadosMap[key].push(o);
      }

      if (o.status_viagem === "executado" && o.prefixo) {
        const key = `Executado: ${o.prefixo} | ${o.placa}`;
        if (!executadosMap[key]) executadosMap[key] = [];
        executadosMap[key].push(o);
      }

      if (o.status_viagem === "terceiro") {
        const key = `Terceiro: ${o.nome_terceiro}`;
        if (!terceirosMap[key]) terceirosMap[key] = [];
        terceirosMap[key].push(o);
      }
    });

    return {
      "Pré-Reservados": preReservados,
      ...reservadosMap,
      ...executadosMap,
      ...terceirosMap,
    };
  }, [orcamentos]);

  /* -------------------- DIAS DO MÊS -------------------- */

  const totalDays = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();
    return new Date(y, m + 1, 0).getDate();
  }, [currentMonth]);

  const monthDays = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = currentMonth.getMonth();

    return Array.from({ length: totalDays }, (_, i) => ({
      date: new Date(y, m, i + 1),
      number: i + 1,
    }));
  }, [currentMonth, totalDays]);

  const isInRange = (day, o) =>
    day >= o.data_inicial && day <= o.data_final;

  /* -------------------- MODAL -------------------- */

  const handleClickDay = async (id) => {
    const res = await api3001.get(`/api/orcamento/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    setShowOrcamentoDetails(res.data);
  };

  /* -------------------- NAVEGAÇÃO -------------------- */

  const prevMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );

  const nextMonth = () =>
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );

  const getDiaSemana = (date) =>
    date.toLocaleDateString("pt-BR", { weekday: "short" });

  /* -------------------- RENDER -------------------- */

  return (
    <Painel $focus={$focus}>
      <Titulo $focus={$focus}>Agenda</Titulo>

      <FullScreen
        onClick={() => {
          $setFocus(!$focus);
        }}
        $focus={$focus}
      />

      <Controls>
        <Btn onClick={prevMonth}>◀</Btn>

        <Strong>
          {currentMonth.toLocaleString("pt-BR", {
            month: "long",
            year: "numeric",
          })}
        </Strong>

        <Btn onClick={nextMonth}>▶</Btn>
      </Controls>

      <TabelaWrapper $focus={$focus}>
        <Tabela>
          <Linha>
            <CornerCell />

            {monthDays.map(({ date, number }) => (
              <HeaderCell key={number}>
                {number}
                <div style={{ fontSize: 10 }}>{getDiaSemana(date)}</div>
              </HeaderCell>
            ))}
          </Linha>

          {Object.entries(grouped).map(([label, items]) => (
            <Linha key={label}>
              <FixedColumn>{label}</FixedColumn>

              {monthDays.map(({ date, number }) => {
                const reservas = items.filter((o) => isInRange(date, o));
                const tipo = reservas[0]?.status_viagem || "normal";

                return (
                  <Celula key={number} tipo={tipo}>
                    {reservas.map((r) => (
                      <ReservationBlock
                        key={r.id}
                        tipo={r.status_viagem}
                        onClick={() => handleClickDay(r.id)}
                      >
                        {r.cliente_nome}
                      </ReservationBlock>
                    ))}
                  </Celula>
                );
              })}
            </Linha>
          ))}
        </Tabela>
      </TabelaWrapper>

      {showOrcamentoDetails && (
        <OrcamentoDetailsModal
          $load={reload}
          orcamento={showOrcamentoDetails}
          onClose={() => setShowOrcamentoDetails(null)}
        />
      )}

      <Legend>
        <LegendItem>
          <LegendColor color={COLORS.reservado} />
          Reservado
        </LegendItem>

        <LegendItem>
          <LegendColor color={COLORS.prereservado} />
          Pré-Reservado
        </LegendItem>

        <LegendItem>
          <LegendColor color={COLORS.executado} />
          Executado
        </LegendItem>

        <LegendItem>
          <LegendColor color={COLORS.terceiro} />
          Terceiro
        </LegendItem>
      </Legend>
    </Painel>
  );
};

export default Agenda;